import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2, ArrowRight } from "lucide-react";
import { Logo } from "@/components/Brand";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";

export default function Login() {
  const { applyAuth } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      applyAuth(data);
      navigate(data.user.role === "admin" ? "/admin" : "/dashboard");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell title="Welcome back" subtitle="Sign in to your Catalina workspace.">
      <form onSubmit={submit} className="space-y-4" data-testid="login-form">
        <div className="space-y-2">
          <Label className="text-xs text-muted-foreground">Email</Label>
          <Input data-testid="login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
            required placeholder="you@company.com" className="bg-card border-border h-11" />
        </div>
        <div className="space-y-2">
          <Label className="text-xs text-muted-foreground">Password</Label>
          <Input data-testid="login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
            required placeholder="••••••••" className="bg-card border-border h-11" />
        </div>
        {error && <p data-testid="login-error" className="text-sm text-kinetic font-mono">{error}</p>}
        <Button data-testid="login-submit" type="submit" disabled={loading}
          className="w-full h-11 bg-white text-black hover:bg-zinc-200 font-medium">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Sign in <ArrowRight className="w-4 h-4 ml-1" /></>}
        </Button>
      </form>
      <p className="text-sm text-muted-foreground mt-6 text-center">
        No account? <Link to="/register" data-testid="to-register" className="text-white hover:text-kinetic transition-colors">Create one</Link>
      </p>
    </AuthShell>
  );
}

export function AuthShell({ title, subtitle, children }) {
  return (
    <div className="min-h-screen flex bg-background">
      <div className="hidden lg:flex flex-col justify-between w-1/2 border-r border-border p-12 relative overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-30" />
        <div className="absolute inset-0" style={{ background: "radial-gradient(circle at 30% 40%, rgba(255,59,48,0.12), transparent 60%)" }} />
        <div className="relative"><Logo /></div>
        <div className="relative">
          <p className="font-mono text-xs text-kinetic mb-4">// bring your own keys</p>
          <h2 className="font-display text-4xl font-bold leading-tight max-w-sm">
            One prompt away from a working full-stack app.
          </h2>
        </div>
        <div className="relative font-mono text-xs text-muted-foreground">$ catalina build "a crm for florists"</div>
      </div>
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-sm fadeup">
          <div className="lg:hidden mb-8"><Logo /></div>
          <h1 className="font-app text-2xl font-semibold mb-1">{title}</h1>
          <p className="text-sm text-muted-foreground mb-8">{subtitle}</p>
          {children}
        </div>
      </div>
    </div>
  );
}
